import { useEffect, useRef, useState } from "react";
import ModalComponent from '../../../../components/common-components/modal/ModalComponent'
import Icons from "../../../../components/icons/Icons";
import General from "../../../../libs/utility/General";
import Avatar from "../../../../components/common-components/avatar";
import { getFormattedPatientAgeDob } from "../../../../libs/patientUtils";
import { useReactToPrint } from 'react-to-print';
import LinkToBase64 from "./LinkToBase64";
import html2canvas from 'html2canvas';
import { useDispatch, useSelector } from "react-redux";
import { patchVitalsTest } from "./VitalsTestSaga";
import { useParams } from "react-router-dom";
import { componentKey } from "./VitalsTestSlice";
import { componentKey as ProviderGroup } from "../../../../containers/SettingsTab/MyAccountCard/Tabs/ProviderGroup/ProviderGroupSlice";

import { jsPDF } from 'jspdf';
import { getProviderGroupDetails } from "../../../../containers/SettingsTab/MyAccountCard/Tabs/ProviderGroup/ProviderGroupSaga";
import SignaturePadWrapper from "../../../../components/common-components/SignaturePad/SignaturePadWrapper";

function ViewReportModal({ open, close, viewTestsData }) {

	const dispatch = useDispatch();
	const pdfRef = useRef();
	const [isRendered, setIsRendered] = useState(false);
    const { patientId } = useParams()

    const { paginationState } = useSelector((state) => state[componentKey])
	const { providerGroupDetails } = useSelector((state) => state[ProviderGroup]);

	const handlePrint = useReactToPrint({
		content: () => pdfRef.current,
		documentTitle: `Health Check-up Report - ${viewTestsData?.patient?.firstName} ${viewTestsData?.patient?.lastName}`,
		onBeforeGetContent: () => {
			return new Promise((resolve) => {
				setTimeout(() => {
					const container = pdfRef.current;
					if (container) {
						const testSections = container.querySelectorAll('.print-test-section');
						testSections.forEach((section, index) => {
							const images = section.querySelectorAll('img, canvas, svg');
							if (images.length > 0) {
								section.classList.add('print-image-section');
                                images.forEach(img => {
									img.classList.add('print-image');
								});
							}
						});
					}
					resolve();
				}, 200);
			});
		},
		onAfterPrint: () => {
			console.log('Print completed');
		},
		removeAfterPrint: false,
		suppressErrors: true,
		print: async (printIframe) => {
			const document = printIframe.contentDocument;
			if (document) {
				const html = document.getElementsByTagName('html')[0];
				html.classList.add('print-mode');
				const style = document.createElement('style');
				style.textContent = `
                    @media print {
                        @page {
                            size: A4 portrait;
                            margin: 10mm;
                        }
                        
                        * {
                            page-break-inside: auto !important;
                            break-inside: auto !important;
                        }
                        
                        .bg-gray-300, .print-test-section {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                        
                        img, canvas, svg {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                            max-width: 100% !important;
                            max-height: 80vh !important;
                            height: auto !important;
                            object-fit: contain !important;
                        }
                        
                        .print-test-section:has(img):not(:first-child),
                        .print-test-section:has(canvas):not(:first-child),
                        .print-test-section:has(svg):not(:first-child) {
                            page-break-before: auto !important;
                            break-before: auto !important;
                        }
                        
                        .flex.flex-wrap {
                            page-break-inside: auto !important;
                            break-inside: auto !important;
                        }
                        
                        .print-test-section {
                            orphans: 2 !important;
                            widows: 2 !important;
                        }
                        
                        /* Optimize image sizing for better fit */
                        img, canvas, svg {
                            max-height: 88vh !important;
                            max-width:98vw !important;
                            width: auto !important;
                            height: auto !important;
                            object-fit: contain !important;
                        }
                        
                        /* Specific handling for image containers */
                        .print-test-section:has(img) {
                            max-height: 95vh !important;
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                            overflow: hidden !important;
                            border-bottom: 28px solid #d1d5db !important;
                        }
                        
                        /* Reduce logo and hospital name size for PDF */
                        .bg-gradient-to-r img {
                            max-width: 240px !important;
                            max-height: 120px !important;
                            width: auto !important;
                            height: auto !important;
                        }
                        
                        .bg-gradient-to-r h1 {
                            font-size: 20px !important;
                            font-weight: bold !important;
                            margin-top: 8px !important;
                        }
                    }
                `;
				document.head.appendChild(style);
			}
			printIframe.contentWindow.print();
        }
	});

	useEffect(() => {
		if (open) {
			setTimeout(() => {
				setIsRendered(true);
			}, 1000);
		} else {
			setIsRendered(false);
		}
	}, [open]);

	useEffect(() => {
		const style = document.createElement('style');
		style.textContent = `
            @media print {
                /* Hide print button when printing */
                button {
                    display: none !important;
                }
                
                /* Ensure proper page setup */
                @page {
                    size: A4 portrait;
                    margin: 10mm;
                }
                
                /* Smart page break logic - only break when necessary */
                * {
                    page-break-inside: auto !important;
                    break-inside: auto !important;
                }
                
                /* Prevent images from breaking */
                img, canvas, svg {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                    max-width: 100% !important;
                    max-height: 88vh !important;
                    max-width:98vw !important;
                    height: auto !important;
                    object-fit: contain !important;
                }
                
                /* Ensure test sections don't break internally */
                .bg-gray-300, .print-test-section {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                    background-color: #d1d5db !important;
                }
                
                /* Specific handling for image test sections */
                .print-test-section:has(img) {
                    max-height: 95vh !important;
                    overflow: hidden !important;
                    page-break-before: always !important;
                    break-before: always !important;
                    border-bottom: 28px solid #d1d5db !important;
                }
                
                /* Specific handling for urine test tables - reduce height and ensure page breaks */
                .print-test-section.urine-test-table {
                    max-height: 95vh !important;
                    overflow: hidden !important;
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                    page-break-before: auto !important;
                    break-before: auto !important;
                    border-bottom: 16px solid #d1d5db !important;
                }
                
                /* Compact styling for urine test tables */
                .print-test-section.urine-test-table .urine-test-content {
                    font-size: 10px !important;
                    line-height: 1.1 !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content div {
                    padding: 1px 2px !important;
                    margin: 0px !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .flex {
                    margin: 0px !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .flex-row {
                    padding: 0px 1px !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .font-semibold {
                    font-size: 10px !important;
                    font-weight: 600 !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .text-base {
                    font-size: 10px !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .p-1 {
                    padding: 1px 2px !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .p-2 {
                    padding: 2px 4px !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .my-3 {
                    margin: 0px !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .mx-1 {
                    margin: 0 1px !important;
                }
                
                /* Force page break for urine test tables that don't fit */
                .print-test-section.urine-test-table:not(:first-child) {
                    page-break-before: auto !important;
                    break-before: auto !important;
                }
                
                /* Ensure urine test content doesn't break across pages */
                .urine-test-content {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }
                
                /* Specific styling for UrineTest13 to reduce white space */
                .print-test-section.urine-test-table:has(.urine-test-content) {
                    max-height: none !important;
                    overflow: visible !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content {
                    max-height: none !important;
                    overflow: visible !important;
                }
                
                /* Grid layout for UrineTest13 in print */
                .print-test-section.urine-test-table .urine-test-content .grid {
                    display: grid !important;
                    grid-template-columns: repeat(3, 1fr) !important;
                    gap: 2px !important;
                }
                
                .print-test-section.urine-test-table .urine-test-content .text-xs {
                    font-size: 9px !important;
                }
                
                /* Print-specific classes for image sections */
                .print-image-section {
                    max-height: 95vh !important;
                    overflow: hidden !important;
                    page-break-before: always !important;
                    break-before: always !important;
                    border-bottom: 28px solid #d1d5db !important;
                }
                
                .print-image {
                    max-height: 88vh !important;
                    max-width:98vw !important;
                    width: auto !important;
                    height: auto !important;
                    object-fit: contain !important;
                }
                
                /* Only force page break if content is too large for current page */
                .print-test-section:has(img):not(:first-child),
                .print-test-section:has(canvas):not(:first-child),
                .print-test-section:has(svg):not(:first-child) {
                    page-break-before: auto !important;
                    break-before: auto !important;
                }
                
                /* Force page break only when content would overflow */
                .print-test-section:has(img):where(:not(:first-child)) {
                    page-break-before: auto !important;
                    break-before: auto !important;
                }
                
                /* Maintain background colors and styling */
                .bg-white {
                    background-color: #ffffff !important;
                }
                
                .bg-gradient-to-r {
                    background: linear-gradient(to right, #f3f4f6, #e5e7eb) !important;
                }
                
                /* Ensure text colors are preserved */
                .text-gray-900 {
                    color: #111827 !important;
                }
                
                .text-gray-700 {
                    color: #374151 !important;
                }
                
                .text-gray-600 {
                    color: #4b5563 !important;
                }
                
                .text-red-500 {
                    color: #ef4444 !important;
                }
                
                /* Ensure proper spacing and layout */
                .p-4, .p-8, .p-2 {
                    padding: inherit !important;
                }
                
                .m-2, .m-4 {
                    margin: inherit !important;
                }
                
                /* Ensure grid layout works in print */
                .grid {
                    display: grid !important;
                }
                
                .flex {
                    display: flex !important;
                }
                
                /* Ensure rounded corners are preserved */
                .rounded-lg, .rounded-xl {
                    border-radius: inherit !important;
                }
                
                /* Ensure shadows are preserved */
                .shadow-lg, .shadow-md {
                    box-shadow: inherit !important;
                }
                
                /* Optimize content flow */
                .flex.flex-wrap {
                    page-break-inside: auto !important;
                    break-inside: auto !important;
                }
                
                /* Ensure the main container allows natural flow */
                [ref="pdfRef"] {
                    page-break-inside: auto !important;
                    break-inside: auto !important;
                }
                
                /* Prevent orphaned content */
                .print-test-section {
                    orphans: 2 !important;
                    widows: 2 !important;
                }
            }
            
            /* Print mode specific styles */
            .print-mode {
                background: white !important;
            }
            
            /* Screen-specific styles for UrineTest13 */
            .urine-test-table .urine-test-content {
                font-size: 10px;
                line-height: 1.1;
            }
            
            .urine-test-table .urine-test-content div {
                padding: 1px 2px;
                margin: 0px;
            }
            
            .urine-test-table .urine-test-content .flex {
                margin: 0px;
            }
            
            .urine-test-table .urine-test-content .flex-row {
                padding: 0px 1px;
            }
            
            .urine-test-table .urine-test-content .font-semibold {
                font-size: 10px;
                font-weight: 600;
            }
            
            .urine-test-table .urine-test-content .text-base {
                font-size: 10px;
            }
            
            .urine-test-table .urine-test-content .p-1 {
                padding: 1px 2px;
            }
            
            .urine-test-table .urine-test-content .p-2 {
                padding: 2px 4px;
            }
            
            .urine-test-table .urine-test-content .my-3 {
                margin: 0px;
            }
            
            .urine-test-table .urine-test-content .mx-1 {
                margin: 0 1px;
            }
            
            /* Grid layout for UrineTest13 */
            .urine-test-table .urine-test-content .grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 2px;
            }
            
            .urine-test-table .urine-test-content .text-xs {
                font-size: 9px;
            }
        `;
		document.head.appendChild(style);

		return () => {
			if (document.head.contains(style)) {
				document.head.removeChild(style);
			}
		};
	}, []);

	useEffect(() => {
		dispatch(getProviderGroupDetails());
	}, []);

	const convertToBase64 = async () => {
		const element = pdfRef.current;

		if (element) {
			try {
				const canvas = await html2canvas(element, {
					scale: 2,
					useCORS: true,
					logging: false,
					backgroundColor: '#ffffff',
					scrollX: -window.scrollX,
					scrollY: -window.scrollY,
					windowWidth: document.documentElement.offsetWidth,
                    windowHeight: document.documentElement.offsetHeight
				});

				const imgData = canvas.toDataURL('image/jpeg', 0.8);

				// Create A4 PDF
				const pdf = new jsPDF({
					orientation: 'portrait',
					unit: 'mm',
					format: 'a4',
					compressPdf: true,
				});

				const pageWidth = pdf.internal.pageSize.getWidth();
				const pageHeight = pdf.internal.pageSize.getHeight();
				const margin = 15;
                const contentWidth = pageWidth - (2 * margin);
				const imgWidth = contentWidth;
				const imgHeight = (canvas.height * contentWidth) / canvas.width;
				const pagesNeeded = Math.ceil(imgHeight / (pageHeight - 2 * margin));

				for (let page = 0; page < pagesNeeded; page++) {
					if (page > 0) {
						pdf.addPage();
					}

					const sourceY = page * (pageHeight - 2 * margin) * (canvas.height / imgHeight);
					const sourceHeight = Math.min(
						(pageHeight - 2 * margin) * (canvas.height / imgHeight),
						canvas.height - sourceY
					);

                    const destHeight = Math.min(
                        pageHeight - 2 * margin,
                        imgHeight - page * (pageHeight - 2 * margin)
                    );

					const tempCanvas = document.createElement('canvas');
					const tempCtx = tempCanvas.getContext('2d');
					tempCanvas.width = canvas.width;
					tempCanvas.height = sourceHeight;

                    tempCtx.drawImage(
                        canvas,
                        0, sourceY, canvas.width, sourceHeight,
                        0, 0, canvas.width, sourceHeight
                    );

					const pageImgData = tempCanvas.toDataURL('image/jpeg', 0.8);
					pdf.addImage(pageImgData, 'JPEG', margin, margin, imgWidth, destHeight);
				}

				const pdfBase64 = pdf.output('datauristring');
				const base64String = pdfBase64.split(',')[1];

				const body = {
					pdfData: base64String,
					uuid: viewTestsData?.uuid,
				};

				dispatch(
					patchVitalsTest({
						body,
						patientId,
						paginationState,
					})
				);
			} catch (error) {
                console.error("Error while converting element to PDF:", error);
			}
		} else {
			console.error('Element not found');
		}
	};
	const testLength = viewTestsData?.test?.length || 0;

    const capitalizedFirstName = viewTestsData?.patient?.firstName ? viewTestsData?.patient?.firstName.charAt(0)?.toUpperCase() + viewTestsData?.patient?.firstName?.slice(1) : '';
    const capitalizedLastName = viewTestsData?.patient?.lastName ? viewTestsData?.patient?.lastName.charAt(0)?.toUpperCase() + viewTestsData?.patient?.lastName?.slice(1) : '';
    const formattedDate = General.getformattedDate(viewTestsData?.createdAt)
	const PATIENT_PERSONAL_DATA = {
        "Name": `${capitalizedFirstName} ${capitalizedLastName}`,
        "DOB & Age": getFormattedPatientAgeDob(viewTestsData?.patient?.dob),
        "Email": viewTestsData?.patient?.email
	};
	const PATIENT_PERSONAL_DATA2 = {
        "Gender": viewTestsData?.patient?.gender,
        "Date": formattedDate
    }
	const BMITestRangeObj = {
        "Underweight": "< 18.5",
        "Healthy": "18.5 - 25",
        "Overweight": "25 - 29.9",
        "Obese": "> 30"
    }
	const SPO2TestRangeObj = {
        "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;": "<u style='font-weight: 600; color: black;'>Value</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;",
        "SpO2%&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Pulse rate(Beats per minute)": "Reading",
        "95%-100%&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;60-100": "Normal",
        "95%&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;101-109": "Continue monitoring",
        "93%-94%&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;110-130": "See your health care doctor",
        "92% or lower&nbsp;&nbsp;&nbsp;131 or higher": "Go to the emergency room or call 911",
    }
	const HBTestRangeObj = {
        "Sex": "Low level&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal level",
        "Female": "< 12(anemia)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;12 to 15",
        "Male": "< 13.5(anemia)&nbsp;&nbsp;&nbsp;&nbsp;13.5 to 18",
    }

	const GlucoseTestRangeObj = {
        "Normal": "<140",
        "Prediabetes": "140-200",
        "Diabetes": ">=200"
    }
	const TemperatureTestRangeObj = {
        "Normal Range": "97-99 °F"
    }
	const MeasureBPTestObj = {
        "Systolic&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Diastolic": "",
        "<&nbsp;90&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<&nbsp;60": "Low",
        "90&nbsp;-&nbsp;120&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;60&nbsp;-&nbsp;80": "Normal",
        "120&nbsp;-&nbsp;139&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;80&nbsp;-&nbsp;89": "Pre-hypertension",
        ">&nbsp;139&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;>&nbsp;89": "High"
	};

	const FarVisionTestRangeObj = {
        "1": "Perfect",
        "0.75 - 1": "Slight Impairment",
        "0.50-0.75": "Moderate Impairment",
        " <0.50 ": "High Impairment"
    }

	const NearVisionTestRangeObj = {
        "1": "Perfect",
        "0.75 - 1": "Slight Impairment",
        "0.50-0.75": "Moderate Impairment",
        " <0.50 ": "High Impairment"
    }

	const HBA1CTestRangeObj = {
        "< = 5.6": "Normal",
        "5.7 - 6.4": "Prediabetes",
        "> 6.4": "Diabetes"
    }

	const LipidProfileTestRangeObj = {
        "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;": "<u style='font-weight: 600; color: black;'>Value</u>",
        "Test Name": "Heart Healthy&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;At-Risk&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;  Dangerouse",
        "Cholesterol": "<200&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;200-239&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;>240",
        "Triglycerides": "<150&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;200-499&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;>500",
        "HDL": ">60&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;40-59(M)and50-59(F)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<40(M)and<50(F)",
        "LDL": "<100&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;100-159&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;>160",
        "HDL/LDL": "<2.0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;2.0-5.0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;>5.0",
        "non-HDL": "<130&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;>130"
    }
	const UrineTestRangeObj = {
        "Glucose": "",
        "< 100": "below Normal",
        "100- 249": "Normal",
        "250-499": "Prediabetes",
        ">=500": "Diabetes",
        "Protein": "",
        "< 10": "Negative",
        "10 - 29": "Normal",
        "30 - 299": "Moderate",
        "> = 300": "High"
    }
	const hsCRPTestRangeObj = {
        "< 1.0": "Normal",
        "1.0- 150": "Moderate Risk",
        "> 150": "High Risk",
    }

	const CRPTestRangeObj = {
        "< 1. 0" : "Normal",
        "1.0 - 150" : "Moderate Risk",
        "> 150 ": "High Risk",
    }

	const CKMBTestRangeObj = {
       "< = 5.0" : "Normal",
       "5.0 - 200" : "Moderate Risk",
       " > 200" : "High Risk" 
    }
	const UAlbuminTestRangeObj = {
        "< 20.0" : "Normal",
        "20.0 - 200" : "Microalbuminuria",
        "> 200" : "Macroalbuminuria" 
    }

	const TSHTestRangeObj = {
        "0.5 - 4.15"  : "Normal",
       " < 0.5" : "Hypothyroidism",
       "> = 4.15" : "Hyperthyroidism"
    }
	const UrineTest10RangeObj = {
        "<p style='font-weight: 600; color: black;'>Leukocytes</p>": "<p style='font-weight: 600; color: black;'>Urobilinogen</p>",
        "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "< = 1&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal",
        "15&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "2 - 4&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "70&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal": " > 4&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High",
        "125&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "",
        "500&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High": "",
        "<p style='font-weight: 600; color: black;'>Protein</p>": "<p style='font-weight: 600; color: black;'>Blood</p>",
        "<u style='font-weight: 600; color: black;'>Range </u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative",
        "15&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "10&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace",
        "30&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal": "25&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal",
        "100&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "80&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "> 300&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High": "200&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Large",
        "<p style='font-weight: 600; color: black;'>Ketone</p>": "<p style='font-weight: 600; color: black;'>Glucose</p>",
        "<u style='font-weight: 600; color: black;'>Range  </u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative",
        "5&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "100&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace",
        "15&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Small": "250&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal",
        "40&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "500&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "80 - 180&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Large": "1000&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High"
    }

	const UrineTest13RangeObj = {
        "<p style='font-weight: 600; color: black;'>Leukocytes</p>": "<p style='font-weight: 600; color: black;'>Urobilinogen</p>",
        "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "< = 1&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal",
        "15&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "2 - 4&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "70&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal": " > 4&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High",
        "125&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "",
        "500&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High": "",
        "<p style='font-weight: 600; color: black;'>Protein</p>": "<p style='font-weight: 600; color: black;'>Blood</p>",
        "<u style='font-weight: 600; color: black;'>Range </u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative",
        "15&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "10&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace",
        "30&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal": "25&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal",
        "100&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "80&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "> 300&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High": "200&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Large",
        "<p style='font-weight: 600; color: black;'>Ketone</p>": "<p style='font-weight: 600; color: black;'>Glucose</p>",
        "<u style='font-weight: 600; color: black;'>Range   </u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range </u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative",
        "5&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "100&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace",
        "15&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Small": "250&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal",
        "40&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "500&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "80 - 160&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Large": "1000&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High",
        "<p style='font-weight: 600; color: black;'>Creatinine</p>": "",
        "<u style='font-weight: 600; color: black;'>Range    </u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "",
        "< = 50&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal": "",
        "100&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "",
        "> = 200 &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High": ""
    }

	const URS14RangeObj = {
        "<p style='font-weight: 600; color: black;'>Leukocytes</p>": "<p style='font-weight: 600; color: black;'>Urobilinogen</p>",
        "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "< = 16&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal",
        "15&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "33&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Small",
        "70&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Small": "66&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "125&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "131&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High",
        "500&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High": "",
        "<p style='font-weight: 600; color: black;'>Protein</p>": "<p style='font-weight: 600; color: black;'>Blood</p>",
        "<u style='font-weight: 600; color: black;'>Range </u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative",
        "0.2&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "10&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace",
        "0.3&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal": "25&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Small",
        "1.0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Small": "80&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "3.0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "200&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Large",
        "20.0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Large": "",
        "<p style='font-weight: 600; color: black;'>Ketone</p>": "<p style='font-weight: 600; color: black;'>Glucose</p>",
        "<u style='font-weight: 600; color: black;'>Range  </u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>": "<u style='font-weight: 600; color: black;'>Range</u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<u style='font-weight: 600; color: black;'>Value</u>",
        "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative": "0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Negative",
        "0.5&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace": "5&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Trace",
        "1.5&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Small": "15-30&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Normal",
        "4.0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate": "60&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Moderate",
        "8.0-16.0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Large": "110&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;High",
    }

	const BhcgTestRangeObj = {
        "< = 5.0" : "No",
        "5.0 - 50" : "3 Weeks",
        "5.0 - 426" : "4 Weeks",
        " > 426" : "> 5 Weeks"
    }

	const testNameToValueKeys = {
        "TemperatureTest": ["result", "unit"],
        "GlucoseTest": ["result", "unit"],
        "HBTest": ["result", "unit"],
        "FarVisionTest": ["result"],
        "NearVisionTest": ["NearVisionResult"],
        "HBA1CTest": ["result", "unit"],
        "hsCRPTest": ["rdtResult", "unit"],
        "CRPTest": ["rdtResult", "CRPTestunit"],
        "BhcgTest": ["rdtResult", "bhgunit"],
        "CKMBTest": ["rdtResult", "unit"],
        "DengueTest": ["rdtResult"],
        "MalariaTest": ["rdtResult"],
        "SyphilisTest": ["rdtResult"],
        "HepatitisBTest": ["rdtResult"],
        "HepatitisCTest": ["rdtResult"],
        "ChikungunyaTest": ["rdtResult"],
        "TyphoidTest": ["rdtResult"],
        "DengueDuoTest": ["rdtResult"],
        "DengueAgTest": ["rdtResult"],
        "PregnancyTest": ["rdtResult"],
        "CovidTest": ["Covidresult"],
        "HIVTest": ["rdtResult"],
        "UAlbuminTest": ["rdtResult", "ualunit"],
        "ElephantiasisTest": ["rdtResult"],
        "SPneumoniaTest": ["rdtResult"],
        "StreptococcusATest": ["rdtResult"],
        "RSVTest": ["rdtResult"],
        "ScrubtyphusAbTest": ["rdtResult"],
        "TSHTest": ["rdtResult", "tshunit"],
        "NicotineTest": ["rdtResult"],
        "LegionellaTest": ["rdtResult"]

	};
	const testbase64 = {
        "ECGTest": ["ecg"],
        "OtoscopeTest": ["otoscope"],
        "StethoscopeTest": ["stethoscope"],
        "DermascopeTest": ["dermascope"],
    }

	const manyTests = {
        "MeasureBP": ["Syslable", "systolic", "unit", "Dialable", "diastolic", "unit"],
        "SPO2Test": ["SPO2lable", "spo2value", "spounit", "pulseratelable", "pulserate", "pulserateunit"],
        "BMITest": ["BMIlable", "bmiValue", "bmiunit", "Heightlable", "heightValue", "heightUnit", "Weightlable", "weightValue", "weightUnit"],
        "UrineTest": ["GlucoseLabel", "Glucosevalue", "unit", "protien", "Protienvalue", "unit"],
        "UrineTest13": ["leuValueLabel", "leuValue", "leuUnit", "nitLabel", "nitValue", "nitUnit", "uroLabel", "uroValue", "uroUnit", "proLabel", "proValue", "proUnit", "phLabel", "phValue", "phUnit", "bloLabel", "bloValue", "bloUnit", "bilLabel", "bilValue", "bilUnit", "ketoneLabel", "ketValue", "ketUnit", "GlucoseLabel", "gluValue", "gluUnit", "cre13Label", "cre13Value", "cre13Unit", "micro13Label", "micro13Value", "", "asc13Label", "asc13Value", "" , "sgLabel", "sgValue", "sgUnit"],
        "LipidProfileTest": ["totalCHLabel", "totalCHValue", "totalCHUnit", "hdlLabel", "hdlValue", "hdlUnit", "ldlLabel", "ldlValue", "ldlUnit", "triglycerideLabel", "triglycerideValue", "triglycerideUnit", "nonHdlLabel", "nonHdlValue", "nonHdlUnit", "hdlRatioLabel", "hdlRatioValue", "hdlRatioUnit"],
        "UrineTest10": ["leuValueLabel", "leuValue", "leuUnit", "nitLabel", "nitValue", "nitUnit", "uroLabel", "uroValue", "uroUnit", "proLabel", "proValue", "proUnit", "phLabel", "phValue", "phUnit", "bloLabel", "bloValue", "bloUnit", "sgLabel", "sgValue", "sgUnit", "ketoneLabel", "ketValue", "ketUnit", "bilLabel", "bilValue", "bilUnit", "GlucoseLabel", "gluValue", "gluUnit"],
        "URS14": ["leuValueLabel", "leuValue", "leuUnit", "nitLabel", "nitValue", "nitUnit", "uroLabel", "uroValue", "uroUnit", "MicroalbuminLabel", "MicroalbuminValue", "Microalbuminnit", "proLabel", "proValue", "proUnit", "phLabel", "phValue", "phUnit", "bloLabel", "bloValue", "bloUnit", "sgLabel", "sgValue", "sgUnit", "AscorbateLabel", "AscorbateValue", "AscorbateUnit", "CreatinineLabel", "CreatinineValue", "CreatinineUnit", "ketoneLabel", "ketValue", "ketUnit", "bilLabel", "bilValue", "bilUnit", "GlucoseLabel", "gluValue", "gluUnit", "CalciumLabel", "CalciumValue", "CalciumUnit"]
    }
	const testNameToRangeObj = {
        "BMITest": BMITestRangeObj,
        "SPO2Test": SPO2TestRangeObj,
        "HBTest": HBTestRangeObj,
        "GlucoseTest": GlucoseTestRangeObj,
        "TemperatureTest": TemperatureTestRangeObj,
        "MeasureBP": MeasureBPTestObj,
        "FarVisionTest": FarVisionTestRangeObj,
        "HBA1CTest": HBA1CTestRangeObj,
        "LipidProfileTest": LipidProfileTestRangeObj,
        "NearVisionTest": NearVisionTestRangeObj,
        "UrineTest": UrineTestRangeObj,
        "hsCRPTest": hsCRPTestRangeObj,
        "CRPTest": CRPTestRangeObj,
        "CKMBTest": CKMBTestRangeObj,
        "UAlbuminTest": UAlbuminTestRangeObj,
        "TSHTest": TSHTestRangeObj,
        "UrineTest10": UrineTest10RangeObj,
        "UrineTest13": UrineTest13RangeObj,
        "URS14": URS14RangeObj,
        "BhcgTest": BhcgTestRangeObj
	};


	return (
		<div>
			<ModalComponent
				open={open}
				icon={<Icons iconName="closeIcon" />}
				close={close}
				customClasses="w-[1100px] overflow-visible"
				hideOverflow={false}
			>
				<div className="p-4" ref={pdfRef}>
					<div className="flex justify-center mt-4">
                        <span className="font-bold text-red-500">
                            Health Check-up Report
                        </span>
					</div>
					<div className="p-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl shadow-lg mt-4 mx-2">
                            <div className="flex justify-between items-start gap-8">
                                {providerGroupDetails?.logopath ? ( <Avatar customClass="w-40 h-20" url={providerGroupDetails?.logopath} />) : ""}
                            <div /> 
                            <div className="ml-auto">
                                <h1 className="text-3xl font-extrabold text-gray-800 mt-4">{providerGroupDetails?.groupName || ""}</h1>
                            </div>
                    </div>
						<div className="flex justify-between items-start">
							<div className="text-start">
                                <p className="text-gray-700 font-semibold mt-2">{providerGroupDetails?.phone || ""}</p>
                                <p className="text-gray-700 mt-1">{providerGroupDetails?.email || ""}</p>
								<p className="text-gray-700  mt-1">
									{[
										providerGroupDetails?.physicalAddress?.address_line_1,
										providerGroupDetails?.physicalAddress?.address_line_2,
										providerGroupDetails?.physicalAddress?.city,
										providerGroupDetails?.physicalAddress?.state,
										providerGroupDetails?.physicalAddress?.country,
										providerGroupDetails?.physicalAddress?.zip,
									]
										.filter(Boolean)
										.join(', ')}
								</p>
							</div>

							<div className="text-end">
								<p className="text-gray-800 mt-1 font-bold text-lg whitespace-nowrap">{`Dr. ${viewTestsData?.provider?.firstName} ${viewTestsData?.provider?.lastName}`}</p>
								<p className="text-gray-700 mt-1">{viewTestsData?.provider?.email || ''}</p>
							</div>
						</div>

					</div>

					<div className="bg-gray-300 rounded-lg shadow-md p-4 h-full overflow-auto mt-4 mx-2">
						<div className="grid grid-cols-12 gap-4">
							<div className="col-span-6 bg-gray-300 pl-2 rounded">
								{Object.entries(PATIENT_PERSONAL_DATA).map(([key, value]) => (
									<div key={key} className="flex flex-row my-3 justify-start items-center">
                                        <span className="w-[20%] font-semibold text-gray-900 text-left mr-1 text-base">{key}
										</span>
										<span className="pr-4"> : </span>
										<div className="w-[60%] font-semibold text-gray-600 text-left mr-1 text-base pl-4">
                                            {value || "-"}
										</div>
									</div>
								))}
							</div>
							<div className="col-span-4 bg-gray-300 rounded">
								{Object.entries(PATIENT_PERSONAL_DATA2).map(([key, value]) => (
									<div key={key} className="flex flex-row my-3 justify-start items-center">
                                        <span className="w-[20%] font-semibold text-gray-900 text-left mr-1 text-base">{key}</span>
										<div className="pr-4"> : </div>
                                        <div className="w-[60%] font-semibold text-gray-600 text-left mr-1 text-base">                                            {value || "-"}
										</div>
									</div>
								))}
							</div>
							<div className="col-span-2 bg-gray-300 p-2 rounded">
								{/* <Avatar customClass="w-[100px] h-[100px] overflow-hidden object-cover"
                                    url={viewTestsData?.patient?.profilePicture} name={`${viewTestsData?.patient?.firstName} ${viewTestsData?.patient?.lastName}`} /> */}
							</div>
						</div>
					</div>

					<div className="flex flex-wrap column-gap-10px mx-2">

						{Array.from({ length: testLength }).map((_, index) => {
							const testName = viewTestsData?.test[index]?.testName;
                            const testResult = viewTestsData?.testResult?.find(result => {
								return result?.name === testName;
							});

							const valuekeysOfManyTests = manyTests[testName] || [];
                            const testValuesOfManyTests = valuekeysOfManyTests.map(key => testResult?.[key] ?? "")

							const keytocompareOfManyTests = Object.keys(manyTests).map((item, index) => {
								return item;
							});

							const valueKeys = testNameToValueKeys[testName] || [];
                            const testValues = valueKeys.map(key => testResult?.[key] ?? "-");

							const testRange = testNameToRangeObj[testName] || {};
							const rangeEntries = Object.entries(testRange);

							const urlToBase64 = testbase64[testName] || [];
                            const urlArray = urlToBase64.map(key => viewTestsData?.[key] ?? "-");

							return (
								<div key={index} className={`bg-gray-300 h-auto rounded-lg shadow-md ${testName === 'UrineTest13' ? 'px-1 py-1' : 'px-2 py-2'} h-full overflow-auto mt-4 print-test-section w-full ${testName === 'UrineTest10' ||testName === 'UrineTest13' ||testName === 'UrineTest14' ||testName === 'URS14'? 'urine-test-table': ''}`}
>
									<div className={`flex justify-center font-semibold text-gray-900 text-left text-base ${testName === 'UrineTest13' ? 'p-1' : 'p-2'}`}>{testName}</div>

                                    <div className={testName === 'UrineTest10' || testName === 'UrineTest13' || testName === 'UrineTest14' || testName === 'URS14' ? 'urine-test-content' : ''}>
										{urlArray?.length !== 0 ? (
											testName !== 'StethoscopeTest' ? (<div className="h-full overflow-auto">
													<LinkToBase64 linkurl={urlArray} />
												</div>) : (<p className="font-semibold text-gray-600 pl-3"> Test Completed! </p>)
										) : keytocompareOfManyTests.includes(testResult?.name) ? (
											<div className="max-w-100 max-h-60 overflow-y-auto column-gap-8px flex-col">
												<div className={`font-semibold bg-white ${testName === 'UrineTest13' ? 'p-0' : 'p-1'}`}>Results : </div>
												<div className={`flex flex-column flex-wrap bg-white ${testName === 'UrineTest13' ? 'p-0' : 'p-1'}`}>
													{testName === 'UrineTest13' ? (
														<div className="grid grid-cols-3 gap-1 w-full">
															{testValuesOfManyTests.map((value, i) =>
																	i % 3 === 0 && (
																		<div key={i}className="flex items-center font-semibold p-0 text-xs">
																			<div className="mr-1">{`${value} :`}</div>
                                                                    {testValuesOfManyTests[i + 1] && <div className="mr-1">{testValuesOfManyTests[i + 1]}</div>}
                                                                    {testValuesOfManyTests[i + 2] && <div>{testValuesOfManyTests[i + 2]}</div>}
																		</div>
																	)
															)}
														</div>
													) : (
														testValuesOfManyTests.map(
															(value, i) =>
																i % 3 === 0 && (
																	<div
																		key={i}
																		className={`flex ${
																			testName === 'BMITest' ||
																			testName === 'MeasureBP' ||
																			testName === 'UrineTest'
																				? 'flex-1'
																				: 'w-1/3'
																		} items-center font-semibold p-1`}
																	>
																		<div className="mr-1">{`${value} :`}</div>
																		{testValuesOfManyTests[i + 1] && (
																			<div className="mr-1">
																				{testValuesOfManyTests[i + 1]}
																			</div>
																		)}
																		{testValuesOfManyTests[i + 2] && (
																			<div>{testValuesOfManyTests[i + 2]}</div>
																		)}
																	</div>
																)
														)
													)}
												</div>
											</div>
										) : (
											testValues.map((value, i) =>
													i % 2 === 0 && (
                                                            <div key={i} className="flex flex-1 items-center font-semibold bg-white p-1 rounded">
															<div className="mr-1">{`Result: - ${value}`}</div>
															{testValues[i + 1] && <div>{testValues[i + 1]}</div>}
														</div>
													)
											)
										)}
									</div>

									{Object.keys(testNameToRangeObj).includes(testResult?.name) ? (
                                        testName !== 'UrineTest10' && testName !== 'UrineTest13' && testName !== 'URS14' && testName !== 'LipidProfileTest' && testName !== 'SPO2Test' && testName !== 'HBTest' ? <div className="col-span-5 font-semibold bg-white p-2 rounded mt-4">
												<div className="flex flex-row my-3 mx-1 justify-start items-center">
                                                <div className="flex-1 font-semibold underline decoration-grey-500 text-left mr-1">Range</div>
                                                <div className="flex-1 font-semibold underline decoration-grey-500 text-left">Value</div>
												</div>
												{rangeEntries.map(([key, value], i) => (
                                                <div key={i} className="flex flex-row my-3 mx-1 justify-start items-center">
														<div className="flex-1 font-semibold text-gray-600 text-left mr-1 text-base">
															<span dangerouslySetInnerHTML={{ __html: key }} />
														</div>
														<div className="flex-1 text-gray-600 text-base font-bold text-left">
															{/* <span className="mr-4"> :</span> */}
                                                        <span dangerouslySetInnerHTML={{ __html: value || " " }} />
														</div>
													</div>
												))}
                                        </div> : <div className={`col-span-5 font-semibold bg-white p-2 rounded mt-4 ${testName === 'UrineTest10' || testName === 'UrineTest13' || testName === 'UrineTest14' || testName === 'URS14' ? 'urine-test-content' : ''}`}>

												{rangeEntries.map(([key, value], i) => (
													<div key={i} className={`flex flex-row ${testName === 'UrineTest13' ? 'my-1' : 'my-3'} mx-1 justify-start items-center`}>
														<div className="flex-1 font-semibold text-gray-600 text-left mr-1 text-base">
															<span dangerouslySetInnerHTML={{ __html: key }} />
														</div>
														<div className="flex-1 text-gray-600 text-base font-bold text-left">
                                                        <span dangerouslySetInnerHTML={{ __html: value || " " }} />
														</div>
													</div>
												))}
											</div>
                                        ) : null}
								</div>
							);
						})}


					</div>
                    <div className="font-bold text-red-500 m-2">
                        Remark
                    </div>
					<div className="bg-gray-300 rounded-lg shadow-md p-2 h-full overflow-auto">
						{viewTestsData?.review}
					</div>
                    {viewTestsData?.provider?.signature && <div className="pt-6 w-full flex justify-end">
                    <SignaturePadWrapper viewProfile={true} clearButton={false} defaultSignature={viewTestsData?.provider?.signature}
					/>
                </div>
                }
				</div>              

				<div className="flex justify-end mt-4">

                    {viewTestsData?.pdfreport === null && <button className="text-met-primary bg-met-secondary border border-met-primary px-3 py-1 rounded-md mr-2" disabled={!isRendered} onClick={convertToBase64}> Send to Kiosk </button>}
                    <button className="text-met-primary bg-met-secondary border border-met-primary px-3 py-1 rounded-md mr-2" disabled={!isRendered} onClick={handlePrint}>Print</button>
				</div>
			</ModalComponent>
		</div>
	);
}

export default ViewReportModal;
